/**
 * AlmostThereNudge - Contextual nudge near category threshold
 *
 * Shows a motivational nudge when accepting the next unreviewed issue
 * would cross a category threshold (safe/caution/high_drama).
 *
 * Design spec:
 * - Detection: Per-issue score estimation against thresholds
 * - Visual: Pulsing glow border on the nudge
 * - Frequency: Max 1 nudge per session (avoids nagging)
 * - Dismissible: Auto-fades after 5 seconds
 *
 * @module components/modern-results/AlmostThereNudge
 */

import { useEffect, useRef, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSequence,
  withRepeat,
  cancelAnimation,
  Easing,
} from 'react-native-reanimated';
import { useTranslation, getDramaCategory } from '@dramadetector/shared';
import type { DramaCategory } from '@dramadetector/shared';
import type { InlineIssue } from './inline-annotations/types';

const AUTO_DISMISS_MS = 5000;

interface AlmostThereNudgeProps {
  /** Current revised score (after accepted issues) */
  revisedScore: number;
  /** Original score from the analysis */
  originalScore: number;
  /** All issues with their current status */
  issues: InlineIssue[];
  /** Whether reduced motion is enabled */
  isReducedMotion?: boolean;
  /** Callback when the nudge issue is tapped */
  onIssueTap?: (issueId: number) => void;
  /** Test ID */
  testID?: string;
}

interface ThresholdNudge {
  issueId: number;
  targetCategory: DramaCategory;
  messageKey: string;
}

/**
 * Check if accepting a pending issue would cross a category threshold.
 *
 * Estimates the new score if a pending issue were accepted using the same
 * proportional reduction formula as calculateRevisedScore in useRevisionTracking.
 */
function findThresholdNudge(
  revisedScore: number,
  originalScore: number,
  issues: InlineIssue[],
): ThresholdNudge | null {
  const currentCategory = getDramaCategory(revisedScore);
  const pendingIssues = issues.filter((i) => i.status === 'pending');
  const totalIssues = issues.length;

  if (totalIssues === 0 || pendingIssues.length === 0) return null;

  const reductionPerIssue = (originalScore / totalIssues) * 0.8;

  for (const issue of pendingIssues) {
    const estimatedNewScore = Math.max(0, revisedScore - reductionPerIssue);
    const newCategory = getDramaCategory(estimatedNewScore);

    if (newCategory !== currentCategory && isBetterCategory(newCategory, currentCategory)) {
      const messageKey = getMessageKey(currentCategory, newCategory);
      if (messageKey) {
        return { issueId: issue.id, targetCategory: newCategory, messageKey };
      }
    }
  }

  return null;
}

function isBetterCategory(a: DramaCategory, b: DramaCategory): boolean {
  const order: Record<DramaCategory, number> = {
    safe: 0,
    caution: 1,
    high_drama: 2,
    extreme_drama: 3,
  };
  return order[a] < order[b];
}

function getMessageKey(from: DramaCategory, to: DramaCategory): string | null {
  if (to === 'safe') return 'almost_safe';
  if (to === 'caution' && (from === 'high_drama' || from === 'extreme_drama')) {
    return 'almost_caution';
  }
  if (to === 'high_drama' && from === 'extreme_drama') return 'almost_high';
  return null;
}

export function AlmostThereNudge({
  revisedScore,
  originalScore,
  issues,
  isReducedMotion,
  onIssueTap,
  testID = 'almost-there-nudge',
}: AlmostThereNudgeProps) {
  const { t } = useTranslation('features');
  const hasShownRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const visibleRef = useRef(false);

  // Animation values (must be declared before any conditional returns)
  const opacity = useSharedValue(0);
  const translateY = useSharedValue(20);
  const glowOpacity = useSharedValue(0.3);

  const containerStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateY: translateY.value }],
  }));

  const glowStyle = useAnimatedStyle(() => ({
    opacity: glowOpacity.value,
  }));

  const nudge = useMemo(
    () => findThresholdNudge(revisedScore, originalScore, issues),
    [revisedScore, originalScore, issues],
  );
  const shouldShow = nudge !== null && !hasShownRef.current;

  const dismiss = useCallback(() => {
    visibleRef.current = false;
    if (!isReducedMotion) {
      opacity.value = withTiming(0, { duration: 400 });
      translateY.value = withTiming(-10, { duration: 400 });
    }
    if (timerRef.current) clearTimeout(timerRef.current);
  }, [isReducedMotion, opacity, translateY]);

  useEffect(() => {
    if (!shouldShow) return;
    if (visibleRef.current) return;

    hasShownRef.current = true;
    visibleRef.current = true;

    if (!isReducedMotion) {
      translateY.value = 20;
      opacity.value = 0;
      translateY.value = withTiming(0, { duration: 300, easing: Easing.out(Easing.ease) });
      opacity.value = withTiming(1, { duration: 300 });

      glowOpacity.value = withRepeat(
        withSequence(
          withTiming(0.6, { duration: 800 }),
          withTiming(0.2, { duration: 800 }),
        ),
        -1,
        true,
      );
    } else {
      opacity.value = 1;
      translateY.value = 0;
    }

    timerRef.current = setTimeout(dismiss, AUTO_DISMISS_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      cancelAnimation(opacity);
      cancelAnimation(translateY);
      cancelAnimation(glowOpacity);
    };
  }, [shouldShow, isReducedMotion, opacity, translateY, glowOpacity, dismiss]);

  // Early return after all hooks
  if (!nudge || (hasShownRef.current && !visibleRef.current)) return null;

  const message = t(`inline_annotations.${nudge.messageKey}`, {
    defaultValue: `Accept to reach ${nudge.targetCategory} zone`,
  });

  const handlePress = () => {
    onIssueTap?.(nudge.issueId);
    dismiss();
  };

  if (isReducedMotion) {
    return (
      <Pressable onPress={handlePress} testID={testID}>
        <View style={[styles.container, styles.staticGlow]}>
          <Text style={styles.text}>{message}</Text>
        </View>
      </Pressable>
    );
  }

  return (
    <Pressable onPress={handlePress} testID={testID}>
      <Animated.View style={[styles.wrapper, containerStyle]}>
        <Animated.View style={[styles.glowBorder, glowStyle]} />
        <View style={styles.container}>
          <Text style={styles.text}>{message}</Text>
        </View>
      </Animated.View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'relative',
    marginTop: 8,
    marginBottom: 4,
    alignSelf: 'center',
  },
  glowBorder: {
    position: 'absolute',
    top: -2,
    left: -2,
    right: -2,
    bottom: -2,
    borderRadius: 14,
    backgroundColor: 'rgba(16, 185, 129, 0.4)',
  },
  container: {
    backgroundColor: 'rgba(16, 185, 129, 0.9)',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 10,
    alignSelf: 'center',
  },
  staticGlow: {
    marginTop: 8,
    marginBottom: 4,
    borderWidth: 2,
    borderColor: 'rgba(16, 185, 129, 0.5)',
  },
  text: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
});
